import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { BookOpen, Plus, Loader2, User, Clock, AlertCircle, MessageSquare, ArrowRightLeft, FileText } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const CATEGORIES = [
  { id: 'Discussion', icon: MessageSquare, color: 'bg-blue-100 text-blue-700' },
  { id: 'Pending', icon: Clock, color: 'bg-amber-100 text-amber-700' },
  { id: 'Handover', icon: ArrowRightLeft, color: 'bg-purple-100 text-purple-700' },
  { id: 'Urgent', icon: AlertCircle, color: 'bg-red-100 text-red-700' },
  { id: 'General', icon: FileText, color: 'bg-stone-100 text-stone-700' },
];

export default function HandoverLog({ clientId }) {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('General');

  const fetchEntries = useCallback(async () => {
    try {
      const res = await api.get(token, `/clients/${clientId}/handover-log`);
      setEntries(res.data.entries || []);
    } catch (err) {
      console.error('Handover log error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await api.post(token, `/clients/${clientId}/handover-log`, { content, category });
      toast.success('Note added to handover log');
      setContent('');
      setCategory('General');
      setShowForm(false);
      fetchEntries();
    } catch (err) {
      toast.error('Failed to add note');
    }
    setSaving(false);
  };

  const getCategoryMeta = (cat) => CATEGORIES.find(c => c.id === cat) || CATEGORIES[4];

  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white" data-testid="handover-log">
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#4A3B32]" />
            <h3 className="font-heading text-base text-[#2C2420]">Handover Log</h3>
            {entries.length > 0 && (
              <Badge className="bg-[#F0EBE6] text-[#4A3B32] text-[10px]">{entries.length}</Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowForm(!showForm)}
            className="text-xs h-8 gap-1"
            data-testid="add-handover-btn"
          >
            <Plus className="w-3 h-3" />
            Add Note
          </Button>
        </div>
        <p className="text-xs text-[#8C8078] mt-1">Structured notes for team handovers and meeting outcomes</p>
      </div>

      {showForm && (
        <div className="px-5 pb-3 border-t border-[#E6E0D9] pt-3 space-y-3" data-testid="handover-form">
          <div className="flex gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="handover-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="What was discussed, what's pending, what the next person needs to know..."
            className="text-sm min-h-[80px] resize-none"
            data-testid="handover-content"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-xs h-8">Cancel</Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={saving || !content.trim()}
              className="text-xs h-8 bg-[#4A3B32] text-white hover:bg-[#3a2e26]"
              data-testid="save-handover-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Save Note
            </Button>
          </div>
        </div>
      )}

      <div className="px-5 pb-5">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" /></div>
        ) : entries.length === 0 ? (
          <div className="text-center py-6 text-sm text-[#8C8078]">
            No handover notes yet. Add the first one to start the log.
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            {entries.map(entry => {
              const meta = getCategoryMeta(entry.category);
              const Icon = meta.icon;
              return (
                <div key={entry.id} className="flex gap-3 group" data-testid={`handover-entry-${entry.id}`}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="w-px flex-1 bg-[#E6E0D9] mt-1" />
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-[#2C2420]">{entry.authorName}</span>
                      <Badge className={`text-[10px] ${meta.color}`}>{entry.category}</Badge>
                      <span className="text-[10px] text-[#8C8078]">
                        {entry.createdAt ? formatDistanceToNow(parseISO(entry.createdAt), { addSuffix: true }) : ''}
                      </span>
                    </div>
                    <p className="text-sm text-[#5D524D] leading-relaxed whitespace-pre-wrap">{entry.content}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
